<script lang="ts">
    import { getContext, onMount } from "svelte";
    import type { Campaign, UpdateCampaignIn } from "../model";
    import { loader, swClient, toast } from "../stores";
    import DropdownComponent from "./components/DropdownComponent.svelte";

    export let brand: {
        name: string;
        logo_url: string;
        ban_url: string;
        popup_url: string;
    } = {
        name: null,
        logo_url: null,
        ban_url: null,
        popup_url: null,
    };
    export let brandList: string[] = [];
    export let campaign: Campaign = null;
    export let brandIndex: number = null;
    export let onClose;

    const { close } = getContext("simple-modal");
    let newBrand: {
        name: string;
        logo_url: string;
        ban_url: string;
        popup_url;
    } = {
        name: null,
        logo_url: null,
        ban_url: null,
        popup_url: null,
    };
    let checkfieldsMsg: string[] = [];

    let checkPopup = false;

    let saveLoading = false;

    onMount(() => {
        newBrand = JSON.parse(JSON.stringify(brand));

        if (newBrand.popup_url) {
            checkPopup = true;
        }
    });

    /**
     * Called when a file is upload; change log or ban url
     * @param e
     * @param type logo or ban
     */
    function onChangeFile(e, type: "logo" | "ban" | "popup") {
        let file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.addEventListener("load", function () {
                if (type === "logo") {
                    (newBrand.logo_url as any) = reader.result;
                } else if (type === "ban") {
                    (newBrand.ban_url as any) = reader.result;
                } else if (type == "popup") {
                    (newBrand.popup_url as any) = reader.result;
                }
            });
            reader.readAsDataURL(file);
        }
    }

    /**
     * Upload a base64 image to solidwind
     * @param image string in b64
     */
    async function _uploadDatafileAsync(image: string): Promise<string> {
        const b64 = image.split("base64,");
        if (b64[1]) {
            // image to base64
            const body = _base64ToArrayBuffer(b64[1]);
            const header: Headers = new Headers();
            header.append("Content-Type", "application/octet-stream");
            try {
                // upload the datafile
                const datafile = await $swClient.post(
                    `/v2/datafiles`,
                    body,
                    header
                );
                return datafile.uid;
            } catch (err) {
                console.error(err);
            }
        }
    }

    /**
     * Base64 to array buffer
     * @param base64 string in base64
     */
    function _base64ToArrayBuffer(base64: string) {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * save edit or creation of a trade brand
     */
    async function saveBrandAsync() {
        saveLoading = true;
        // check if all fields are not empty
        if (_checkFields()) {
            const loaderSaveOpe = $loader.show("Chargement de l'opération");

            // chech if at least one value has changed
            if (
                !(
                    newBrand.name === brand.name &&
                    newBrand.logo_url === brand.logo_url &&
                    newBrand.ban_url === brand.ban_url &&
                    newBrand.popup_url == brand.popup_url &&
                    !newBrand.popup_url &&
                    !checkPopup
                )
            ) {
                const index = campaign.attrs.findIndex((attr) => {
                    return attr.key === "trade_brands";
                });

                //set logo
                if (newBrand.logo_url !== brand.logo_url) {
                    const datafileLogoUid = await _uploadDatafileAsync(
                        newBrand.logo_url
                    );
                    newBrand.logo_url = `${$swClient.uri}/v2/datafiles/${datafileLogoUid}`;
                }
                //set ban
                if (newBrand.ban_url !== brand.ban_url) {
                    const datafileBanUid = await _uploadDatafileAsync(
                        newBrand.ban_url
                    );
                    newBrand.ban_url = `${$swClient.uri}/v2/datafiles/${datafileBanUid}`;
                }

                //set popup
                if (
                    newBrand.popup_url !== brand.popup_url &&
                    newBrand.popup_url
                ) {
                    const datafileBanUid = await _uploadDatafileAsync(
                        newBrand.popup_url
                    );
                    newBrand.popup_url = `${$swClient.uri}/v2/datafiles/${datafileBanUid}`;
                } else if (!newBrand.popup_url && checkPopup) {
                    newBrand.popup_url = newBrand.ban_url;
                }

                if (index !== -1) {
                    const tradeBrands = JSON.parse(campaign.attrs[index].value);
                    if (brandIndex == null) {
                        // creation
                        tradeBrands.push(newBrand);
                    } else {
                        // edition
                        tradeBrands[brandIndex] = newBrand;
                    }
                    campaign.attrs[index].value = JSON.stringify(tradeBrands);
                } else {
                    //create attr if doesn't exist
                    campaign.attrs.push({
                        key: "trade_brands",
                        value: JSON.stringify([newBrand]),
                    });
                }

                let newCampaign: UpdateCampaignIn = {
                    name: campaign.name,
                    type_uid: campaign.type.uid,
                    attrs: campaign.attrs,
                    geo_area_uid: campaign.geo_area.uid,
                    description: campaign.description,
                    cover: campaign.cover,
                };
                try {
                    //update campaign
                    await $swClient.putCampaign(campaign.uid, newCampaign);
                    $toast.success("Sauvegarde effectuée avec succès.");
                    onClose(campaign);
                    close();
                } catch (err) {
                    $toast.error(`Une erreur s'est produite : ${err}.`);
                } finally {
                    $loader.hide(loaderSaveOpe);
                }
            } else {
                close();
            }
        }
        saveLoading = false;
    }

    /**
     * Check if all fields in the form are filled
     * setup error msg if not
     */
    function _checkFields(): boolean {
        let returnValue = true;
        checkfieldsMsg = [];
        if (!newBrand.name) {
            checkfieldsMsg.push("Le nom doit être renseigné.");
            returnValue = false;
        }
        if (!newBrand.logo_url) {
            checkfieldsMsg.push("Le logo doit être renseigné.");
            returnValue = false;
        }
        if (!newBrand.ban_url) {
            checkfieldsMsg.push("La bannière doit être renseignée.");
            returnValue = false;
        }

        return returnValue;
    }

    function usePopup(): void {
        checkPopup = checkPopup ? false : true;
        if (!checkPopup) {
            newBrand.popup_url = "";
        }
    }
</script>

<div class="p-5">
    <div class="field">
        <label for="name" class="label">Nom</label>
        <div class="control">
            <DropdownComponent bind:value={newBrand.name} items={brandList}>
                <div slot="item" let:item>{item}</div>
            </DropdownComponent>
        </div>
    </div>
    <div class="field">
        <label for="logo" class="label">Miniature</label>
        <div class="control">
            <input
                type="file"
                style={!newBrand.logo_url ? "" : "display:none;"}
                id="upload-logo"
                name="avatar"
                accept="image/png, image/jpeg, image/webp"
                on:change={(e) => {
                    onChangeFile(e, "logo");
                }}
            />
            <div
                class="columns items-center"
                style={newBrand.logo_url ? "" : "display:none;"}
            >
                <div class="column">
                    <img class="img" src={newBrand.logo_url} alt="Preview" />
                </div>
                <div class="column">
                    <button
                        class="button is-dark btn-elpev"
                        on:click={() =>
                            document.getElementById(`upload-logo`).click()}
                        ><span title="Edit" class="fas fa-pen" /></button
                    >
                </div>
            </div>
        </div>
    </div>
    <div class="field">
        <label for="ban" class="label">Bannière</label>
        <div class="control">
            <input
                type="file"
                style={!newBrand.ban_url ? "" : "display:none;"}
                id="upload-ban"
                name="avatar"
                accept="image/png, image/jpeg, image/webp"
                on:change={(e) => {
                    onChangeFile(e, "ban");
                }}
            />
            <div
                class="columns items-center"
                style={newBrand.ban_url ? "" : "display:none;"}
            >
                <div class="column">
                    <img class="img" src={newBrand.ban_url} alt="Preview" />
                </div>
                <div class="column">
                    <button
                        class="button is-dark btn-elpev"
                        on:click={() =>
                            document.getElementById(`upload-ban`).click()}
                        ><span title="Edit" class="fas fa-pen" /></button
                    >
                </div>
            </div>
        </div>
    </div>
    <div class="field">
        <label for="pop" class="label pop"> Popup de mise en avant </label>

        <label class="label" style="cursor: pointer;">
            <input
                type="checkbox"
                checked={checkPopup}
                on:change={() => usePopup()}
                style="cursor: pointer;"
            /> Utiliser une Popup
        </label>
        {#if checkPopup}
            <div class="control">
                <input
                    type="file"
                    style={!newBrand.popup_url ? "" : "display:none;"}
                    id="upload-pop"
                    name="avatar"
                    accept="image/png, image/jpeg, image/webp"
                    on:change={(e) => {
                        onChangeFile(e, "popup");
                    }}
                />
                <div
                    class="columns items-center"
                    style={newBrand.popup_url ? "" : "display:none;"}
                >
                    <div class="column">
                        <img
                            class="img"
                            src={newBrand.popup_url}
                            alt="Preview"
                        />
                    </div>
                    <div class="column">
                        <button
                            class="button is-dark btn-elpev"
                            on:click={() =>
                                document.getElementById(`upload-pop`).click()}
                        >
                            <span title="Edit" class="fas fa-pen" />
                        </button>
                        <button
                            class="button is-danger"
                            on:click={() => {
                                newBrand.popup_url = "";
                            }}
                        >
                            <span title="Supprimer" class="fa fa-times" />
                        </button>
                    </div>
                </div>
            </div>
        {/if}
    </div>
    {#if checkfieldsMsg.length > 0}
        <div class="is-danger message">
            <div class="message-body">
                {#each checkfieldsMsg as msg}
                    <li>{msg}</li>
                {/each}
            </div>
        </div>
    {/if}
    <div style="float: right;">
        <button
            class="button is-dark btn-elpev"
            on:click={() => saveBrandAsync()}
            disabled={saveLoading}
            class:is-loading={saveLoading}
        >
            Valider
        </button>
    </div>
</div>

<style lang="scss">
    @import "../styles/variables.scss";

    .img {
        max-height: 100px;
    }
    .items-center {
        align-items: center;
    }
    .pop {
        .icon {
            color: $dark-mauve;
        }
    }
</style>
